<template>
  <div ref="scroller" class="deepa-chat-message-list overflow-y-auto">
    <section
      ref="container"
      :aria-label="t('deepa_chat.title')"
      class="d-flex flex-column ga-4 pa-4 text-body-medium"
    >
      <div
        v-for="(message, index) in messages"
        :key="index"
        class="d-flex flex-column"
        :class="{ 'deepa-chat-message-user': message.role === 'user' }"
        :style="getMessageStyle(message, index)"
      >
        <DeepAChatMessageClient v-if="message.role === 'user'" :message />
        <DeepAChatMessageServer v-else :message />
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { useGoTo } from 'vuetify'
import DeepAChatMessageClient from '@/pocs/chat/components/DeepAChatMessageClient.vue'
import DeepAChatMessageServer from '@/pocs/chat/components/DeepAChatMessageServer.vue'
import type { DeepAChatMessage } from '@/pocs/chat/types'

const props = defineProps({
  messages: {
    type: Array as PropType<DeepAChatMessage[]>,
    default: () => [],
  },
})

const { t } = useI18n()
const goTo = useGoTo()

const scroller = useTemplateRef<HTMLElement>('scroller')
const container = useTemplateRef<HTMLElement>('container')

function isGrowingMessage(message: DeepAChatMessage, index: number) {
  return (
    message.role === 'assistant' &&
    index === props.messages.length - 1 &&
    props.messages[index - 1]?.role === 'user'
  )
}

// keeps the latest question pinned to the top while its reply streams in
function getMessageStyle(message: DeepAChatMessage, index: number) {
  return scroller.value && isGrowingMessage(message, index)
    ? { minHeight: `${scroller.value.clientHeight}px` }
    : undefined
}

function scrollToLastUserMessage() {
  const lastUserMessage = Array.from(
    container.value?.querySelectorAll<HTMLElement>(
      '.deepa-chat-message-user',
    ) ?? [],
  ).at(-1)
  if (scroller.value && lastUserMessage) {
    goTo(lastUserMessage, { container: scroller.value, offset: -8 })
  }
}

watch(() => props.messages.length, scrollToLastUserMessage, {
  immediate: true,
  flush: 'post',
})
</script>

<style lang="scss" scoped>
.deepa-chat-message-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--v-theme-on-surface), 0.2) transparent;
}
</style>
